/**
 * Supervisor (returns) and WHM (item control) report aggregation.
 * Pure functions over data those roles can already read — no API calls here.
 */
import { inDateRange, ReportDateRange } from "./codReports";

/* ══ Returns (Supervisor) ═══════════════════════════════════════════════════ */

export type ReturnBucket = "pending" | "approved" | "rejected" | "cancelled";

export interface ReturnLine {
  itemId: string;
  quantity: number;
  batchNumber: string;
  reason: string;
  price: number;
  grossPrice: number;
}

export interface ReturnRecord {
  id: string;
  returnNumber: string;
  requestDate?: string;
  amount: number;
  status: ReturnBucket;
  customerId: string;
  customerCode: string;
  customerName: string;
  rejectionReason: string;
  rejectionNote: string;
  additionalNotes: string;
  items: ReturnLine[];
}

/** Backend enum: Initiated=0, Pending=1, Approved=2, Rejected=3, Cancelled=4 (sent as number or name). */
export const toReturnBucket = (status: unknown): ReturnBucket => {
  const text = String(status ?? "").trim().toLowerCase();
  if (text === "2" || text === "approved") return "approved";
  if (text === "3" || text === "rejected") return "rejected";
  if (text === "4" || text === "cancelled" || text === "canceled") return "cancelled";
  return "pending";
};

const pick = (raw: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
};

export const lineValue = (line: ReturnLine) =>
  line.grossPrice > 0 ? line.grossPrice : line.price * line.quantity;

export const normalizeReturn = (raw: Record<string, unknown>): ReturnRecord => {
  const itemsRaw = pick(raw, "items", "Items");
  const items: ReturnLine[] = Array.isArray(itemsRaw)
    ? itemsRaw.map((item) => {
        const row = item as Record<string, unknown>;
        return {
          itemId: String(pick(row, "itemId", "ItemId") ?? "").trim(),
          quantity: Number(pick(row, "quantity", "Quantity") ?? 0),
          batchNumber: String(pick(row, "batchNumber", "BatchNumber") ?? ""),
          reason: String(pick(row, "reason", "Reason") ?? "").trim(),
          price: Number(pick(row, "price", "Price") ?? 0),
          grossPrice: Number(pick(row, "grossPrice", "GrossPrice") ?? 0),
        };
      })
    : [];
  const amount = Number(pick(raw, "amount", "Amount") ?? 0);
  return {
    id: String(pick(raw, "id", "Id") ?? ""),
    returnNumber: String(pick(raw, "returnNumber", "ReturnNumber", "title", "Title") ?? ""),
    requestDate: (pick(raw, "requestDate", "RequestDate") as string | undefined) ?? undefined,
    amount: amount > 0 ? amount : items.reduce((sum, line) => sum + lineValue(line), 0),
    status: toReturnBucket(pick(raw, "status", "Status")),
    customerId: String(pick(raw, "customerId", "CustomerId") ?? ""),
    customerCode: String(pick(raw, "customerCode", "CustomerCode") ?? ""),
    customerName: String(pick(raw, "customerName", "CustomerName") ?? ""),
    rejectionReason: String(pick(raw, "rejectionReason", "RejectionReason") ?? ""),
    rejectionNote: String(pick(raw, "rejectionNote", "RejectionNote") ?? ""),
    additionalNotes: String(pick(raw, "additionalNotes", "AdditionalNotes") ?? ""),
    items,
  };
};

export interface ReturnTotals {
  requests: number;
  pending: number;
  approved: number;
  rejected: number;
  cancelled: number;
  requestedValue: number;
  approvedValue: number;
  rejectedValue: number;
  pendingValue: number;
  quantity: number;
  lastRequest?: string;
}

const emptyReturnTotals = (): ReturnTotals => ({
  requests: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
  cancelled: 0,
  requestedValue: 0,
  approvedValue: 0,
  rejectedValue: 0,
  pendingValue: 0,
  quantity: 0,
});

const latest = (a?: string, b?: string) => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
};

const addReturn = (totals: ReturnTotals, record: ReturnRecord) => {
  totals.requests += 1;
  totals[record.status] += 1;
  totals.requestedValue += record.amount;
  if (record.status === "approved") totals.approvedValue += record.amount;
  if (record.status === "rejected") totals.rejectedValue += record.amount;
  if (record.status === "pending") totals.pendingValue += record.amount;
  totals.quantity += record.items.reduce((sum, line) => sum + line.quantity, 0);
  totals.lastRequest = latest(totals.lastRequest, record.requestDate);
};

export const sumReturnTotals = (rows: ReturnTotals[]): ReturnTotals =>
  rows.reduce((acc, row) => {
    (Object.keys(emptyReturnTotals()) as (keyof ReturnTotals)[]).forEach((key) => {
      if (key === "lastRequest") acc.lastRequest = latest(acc.lastRequest, row.lastRequest);
      else (acc[key] as number) += row[key] as number;
    });
    return acc;
  }, emptyReturnTotals());

export const approvalRate = (totals: Pick<ReturnTotals, "approved" | "rejected">) => {
  const decided = totals.approved + totals.rejected;
  return decided > 0 ? totals.approved / decided : 0;
};

export const scopeReturns = (records: ReturnRecord[], range: ReportDateRange) =>
  records.filter((record) => inDateRange(record.requestDate, range));

export interface ReturnCustomerRow extends ReturnTotals {
  key: string;
  customerId: string;
  customerCode: string;
  customerName: string;
  records: ReturnRecord[];
}

export const buildReturnCustomerRows = (records: ReturnRecord[]): ReturnCustomerRow[] => {
  const map = new Map<string, ReturnCustomerRow>();
  records.forEach((record) => {
    const key = record.customerId || record.customerCode || "unknown";
    let row = map.get(key);
    if (!row) {
      row = {
        key,
        customerId: record.customerId,
        customerCode: record.customerCode,
        customerName: record.customerName,
        ...emptyReturnTotals(),
        records: [],
      };
      map.set(key, row);
    }
    row.records.push(record);
    addReturn(row, record);
  });
  return Array.from(map.values()).sort(
    (a, b) => b.pending - a.pending || b.requestedValue - a.requestedValue
  );
};

export interface ReturnProductRow {
  key: string;
  itemId: string;
  name: string;
  requests: number;
  quantity: number;
  value: number;
  approvedQuantity: number;
  rejectedQuantity: number;
  batches: string[];
  reasons: string[];
}

export const buildReturnProductRows = (
  records: ReturnRecord[],
  productName: (itemId: string) => string
): ReturnProductRow[] => {
  const map = new Map<string, ReturnProductRow & { _batches: Set<string>; _reasons: Set<string>; _requests: Set<string> }>();
  records.forEach((record) => {
    record.items.forEach((line) => {
      const key = line.itemId || "unknown";
      let row = map.get(key);
      if (!row) {
        row = {
          key,
          itemId: line.itemId,
          name: productName(line.itemId),
          requests: 0,
          quantity: 0,
          value: 0,
          approvedQuantity: 0,
          rejectedQuantity: 0,
          batches: [],
          reasons: [],
          _batches: new Set(),
          _reasons: new Set(),
          _requests: new Set(),
        };
        map.set(key, row);
      }
      row._requests.add(record.id);
      row.quantity += line.quantity;
      row.value += lineValue(line);
      if (record.status === "approved") row.approvedQuantity += line.quantity;
      if (record.status === "rejected") row.rejectedQuantity += line.quantity;
      if (line.batchNumber) row._batches.add(line.batchNumber);
      if (line.reason) row._reasons.add(line.reason);
    });
  });
  return Array.from(map.values())
    .map(({ _batches, _reasons, _requests, ...row }) => ({
      ...row,
      requests: _requests.size,
      batches: Array.from(_batches),
      reasons: Array.from(_reasons),
    }))
    .sort((a, b) => b.quantity - a.quantity || b.value - a.value);
};

export interface ReturnReasonRow {
  key: string;
  reason: string;
  lines: number;
  quantity: number;
  value: number;
}

export const buildReturnReasonRows = (records: ReturnRecord[]): ReturnReasonRow[] => {
  const map = new Map<string, ReturnReasonRow>();
  records.forEach((record) => {
    record.items.forEach((line) => {
      const reason = line.reason || "—";
      const key = reason.toLowerCase();
      const row = map.get(key) ?? { key, reason, lines: 0, quantity: 0, value: 0 };
      row.lines += 1;
      row.quantity += line.quantity;
      row.value += lineValue(line);
      map.set(key, row);
    });
  });
  return Array.from(map.values()).sort((a, b) => b.quantity - a.quantity);
};

/* ══ Item control (WHM) ═════════════════════════════════════════════════════ */

export type ControlLogicName = "None" | "Consumption" | "Customer";

export interface ItemControlRecord {
  id: string;
  itemId: string;
  itemCode: string;
  itemDescription: string;
  quantityReleased: number;
  stockSite: string;
  releaseDate?: string;
  controlLogic: ControlLogicName;
  maxQtyPerCustomer?: number;
  authorizedCustomerIds: string[];
  batchNumber?: string;
  acceptedBy: string;
  acceptedAt?: string;
}

export interface StockProduct {
  id: string;
  code: string;
  name: string;
  stock: number;
  stockSite: string;
}

/** Backend enum: None=0, Consumption=1, Customer=2 (sent as number or name). */
export const toControlLogic = (value: unknown): ControlLogicName => {
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "1" || text === "consumption") return "Consumption";
  if (text === "2" || text === "customer") return "Customer";
  return "None";
};

export const normalizeItemControl = (raw: Record<string, unknown>): ItemControlRecord => ({
  id: String(pick(raw, "id", "Id") ?? ""),
  itemId: String(pick(raw, "itemId", "ItemId") ?? ""),
  itemCode: String(pick(raw, "itemCode", "ItemCode") ?? "").trim(),
  itemDescription: String(pick(raw, "itemDescription", "ItemDescription") ?? ""),
  quantityReleased: Number(pick(raw, "quantityReleased", "QuantityReleased") ?? 0),
  stockSite: String(pick(raw, "stockSite", "StockSite") ?? "").trim(),
  releaseDate: (pick(raw, "releaseDate", "ReleaseDate") as string | undefined) ?? undefined,
  controlLogic: toControlLogic(pick(raw, "controlLogic", "ControlLogic")),
  maxQtyPerCustomer:
    pick(raw, "maxQtyPerCustomer", "MaxQtyPerCustomer") != null
      ? Number(pick(raw, "maxQtyPerCustomer", "MaxQtyPerCustomer"))
      : undefined,
  authorizedCustomerIds: Array.isArray(pick(raw, "authorizedCustomerIds", "AuthorizedCustomerIds"))
    ? (pick(raw, "authorizedCustomerIds", "AuthorizedCustomerIds") as unknown[]).map(String)
    : [],
  batchNumber: (pick(raw, "batchNumber", "BatchNumber") as string | undefined) || undefined,
  acceptedBy: String(pick(raw, "acceptedBy", "AcceptedBy") ?? ""),
  acceptedAt: (pick(raw, "acceptedAt", "AcceptedAt") as string | undefined) ?? undefined,
});

export const normalizeStockProduct = (raw: Record<string, unknown>): StockProduct => ({
  id: String(pick(raw, "id", "Id") ?? ""),
  code: String(pick(raw, "code", "Code") ?? "").trim(),
  name: String(pick(raw, "name", "Name", "title", "Title") ?? ""),
  stock: Number(pick(raw, "stock", "Stock") ?? 0),
  stockSite: String(pick(raw, "stockSite", "StockSite") ?? "").trim(),
});

const stockKey = (code: string, site: string) => `${code.toLowerCase()}|${site.toLowerCase()}`;

/** Total stock on hand per product code + site (products are one row per code/site/batch). */
export const buildStockIndex = (products: StockProduct[]) => {
  const index = new Map<string, { stock: number; name: string }>();
  products.forEach((product) => {
    if (!product.code) return;
    const key = stockKey(product.code, product.stockSite);
    const entry = index.get(key) ?? { stock: 0, name: product.name };
    entry.stock += product.stock;
    if (!entry.name) entry.name = product.name;
    index.set(key, entry);
  });
  return index;
};

export interface ControlRow extends ItemControlRecord {
  productName: string;
  stockOnHand: number;
  releasedShare: number;
  customerNames: string[];
}

export const buildControlRows = (
  controls: ItemControlRecord[],
  stockIndex: Map<string, { stock: number; name: string }>,
  customerName: (id: string) => string
): ControlRow[] =>
  controls
    .map((control) => {
      const stock = stockIndex.get(stockKey(control.itemCode, control.stockSite));
      const stockOnHand = stock?.stock ?? 0;
      return {
        ...control,
        productName: control.itemDescription || stock?.name || control.itemCode,
        stockOnHand,
        releasedShare: stockOnHand > 0 ? control.quantityReleased / stockOnHand : 0,
        customerNames: control.authorizedCustomerIds.map(customerName),
      };
    })
    .sort((a, b) => a.stockSite.localeCompare(b.stockSite) || a.itemCode.localeCompare(b.itemCode));

export interface SiteRow {
  key: string;
  site: string;
  controls: number;
  customerRestricted: number;
  consumptionCapped: number;
  quantityReleased: number;
  stockOnHand: number;
  productsInStock: number;
  uncontrolledInStock: number;
}

export const buildSiteRows = (rows: ControlRow[], products: StockProduct[]): SiteRow[] => {
  const map = new Map<string, SiteRow>();
  const ensure = (site: string) => {
    const name = site || "—";
    const key = name.toLowerCase();
    let row = map.get(key);
    if (!row) {
      row = {
        key,
        site: name,
        controls: 0,
        customerRestricted: 0,
        consumptionCapped: 0,
        quantityReleased: 0,
        stockOnHand: 0,
        productsInStock: 0,
        uncontrolledInStock: 0,
      };
      map.set(key, row);
    }
    return row;
  };
  const controlled = new Set(rows.map((row) => stockKey(row.itemCode, row.stockSite)));
  rows.forEach((row) => {
    const site = ensure(row.stockSite);
    site.controls += 1;
    if (row.controlLogic === "Customer") site.customerRestricted += 1;
    if (row.controlLogic === "Consumption") site.consumptionCapped += 1;
    site.quantityReleased += row.quantityReleased;
  });
  const seen = new Set<string>();
  products.forEach((product) => {
    if (!product.code) return;
    const site = ensure(product.stockSite);
    site.stockOnHand += product.stock;
    const key = stockKey(product.code, product.stockSite);
    if (product.stock > 0 && !seen.has(key)) {
      seen.add(key);
      site.productsInStock += 1;
      if (!controlled.has(key)) site.uncontrolledInStock += 1;
    }
  });
  return Array.from(map.values()).sort((a, b) => b.controls - a.controls || a.site.localeCompare(b.site));
};

export interface UncontrolledProduct {
  key: string;
  code: string;
  name: string;
  site: string;
  stock: number;
}

export const buildUncontrolledProducts = (
  rows: ControlRow[],
  stockIndex: Map<string, { stock: number; name: string }>
): UncontrolledProduct[] => {
  const controlled = new Set(rows.map((row) => stockKey(row.itemCode, row.stockSite)));
  const out: UncontrolledProduct[] = [];
  stockIndex.forEach((entry, key) => {
    if (controlled.has(key) || entry.stock <= 0) return;
    const [code, site] = key.split("|");
    out.push({ key, code: code.toUpperCase(), name: entry.name, site: site.toUpperCase(), stock: entry.stock });
  });
  return out.sort((a, b) => b.stock - a.stock);
};

export interface CustomerAccessRow {
  key: string;
  customerId: string;
  customerName: string;
  products: { code: string; name: string; site: string; maxQty?: number }[];
}

export const buildCustomerAccessRows = (
  rows: ControlRow[],
  customerName: (id: string) => string
): CustomerAccessRow[] => {
  const map = new Map<string, CustomerAccessRow>();
  rows
    .filter((row) => row.controlLogic === "Customer")
    .forEach((row) => {
      row.authorizedCustomerIds.forEach((id) => {
        const entry = map.get(id) ?? { key: id, customerId: id, customerName: customerName(id), products: [] };
        entry.products.push({
          code: row.itemCode,
          name: row.productName,
          site: row.stockSite,
          maxQty: row.maxQtyPerCustomer,
        });
        map.set(id, entry);
      });
    });
  return Array.from(map.values()).sort((a, b) => b.products.length - a.products.length);
};
